import React, { useCallback, useMemo } from 'react';

import { Center, Group, Loader, Stack, Title } from '@mantine/core';
import BackButton from 'components/BackButton';
import { BillingPlanSelectModal } from 'components/SalesPortal/BillingPlanSelect';
import PackageSummary from 'components/SalesPortal/PackageSummary';
import { useAuth } from 'context/auth-context';
import useSalesPortalConfiguration from 'hooks/salesPortal/useSalesPortalConfiguration';
import useSalesPortalPackage from 'hooks/salesPortal/useSalesPortalPackage';
import Header from 'layouts/SalesPortalLayout/components/Header';
import BillingPlanError from 'pages/sales-portal/SalesPortalBillingPlan/components/Checkout/components/BillingPlanError';
import NotFound from 'pages/shared/NotFound';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BillingPlanWithInstallments } from 'types';
import { preserveQueryParams } from 'utils/navigation';

// Multi-plan packages route through here from the catalog's "Get started" CTA:
// the pre-checkout "Choose a billing plan" modal opens over the package details,
// and selection lands in the checkout with the Billing Plan step pre-completed
// (spec Item 8). Single-plan packages skip this page entirely.
const SalesPortalPackagePage = (): JSX.Element => {
  const { salesPortalSlug, packageId } = useParams<{ salesPortalSlug: string; packageId: string }>();
  const { session } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    data: salesPortalConfig,
    isError: salesPortalConfigError,
    isLoading: salesPortalConfigLoading,
  } = useSalesPortalConfiguration(salesPortalSlug);
  const {
    data: packageData,
    isError: packageError,
    isLoading: packageLoading,
  } = useSalesPortalPackage({
    gym: salesPortalConfig?.data.gym.gymId,
    packageId,
    profileId: session?.profile?.profileId,
  });

  const publicBillingPlans = useMemo(
    () => packageData?.data.billingPlans.filter(plan => plan.salesPortalStatus === 'PUBLIC') ?? [],
    [packageData?.data.billingPlans],
  );

  const handleBackClick = useCallback((): void => {
    if (packageData?.data.salesPortalStatus === 'PRIVATE') {
      const path = `/sales-portal/${salesPortalSlug}`;
      const pathWithQueryParams = preserveQueryParams(path, searchParams);
      window.location.href = pathWithQueryParams;
      return;
    }

    window.history.back();
  }, [packageData?.data.salesPortalStatus, salesPortalSlug, searchParams]);

  const handleBillingPlanSelect = useCallback(
    (billingPlan: BillingPlanWithInstallments): void => {
      navigate(preserveQueryParams(`${billingPlan.billingPlanId}`, searchParams));
    },
    [navigate, searchParams],
  );

  if (salesPortalConfigLoading || packageLoading) {
    return (
      <Center sx={{ flexGrow: 1 }} p='lg'>
        <Loader />
      </Center>
    );
  }

  if (
    salesPortalConfigError ||
    packageError ||
    (salesPortalConfig && !salesPortalConfig.data.enabled) ||
    (packageData && !(packageData.data.salesPortalStatus !== 'DISABLED'))
  ) {
    return <NotFound />;
  }

  // A live package with no purchasable (PUBLIC) plans is the catalog's "Unavailable"
  // case — the CTA routes here, and direct links land here too. Show the dedicated
  // billing-plan error screen (spec Items 4 + 8) instead of an empty plan picker.
  if (packageData?.data && publicBillingPlans.length === 0) {
    return (
      <>
        <Header logo={salesPortalConfig?.data.logo} gymTitle={salesPortalConfig?.data.gym.title} />
        <Stack className='content'>
          <BillingPlanError />
        </Stack>
      </>
    );
  }

  return (
    <>
      <Header logo={salesPortalConfig?.data.logo} gymTitle={salesPortalConfig?.data.gym.title} />
      <Stack className='content'>
        <Group position='apart' mb='md'>
          <BackButton handleOnClick={handleBackClick} />
          <Title order={2}>Package Details</Title>
          <div />
        </Group>
        {packageData?.data && (
          <Stack spacing='lg' px='lg'>
            <PackageSummary pckg={packageData.data} hasBackButton />
          </Stack>
        )}
        <BillingPlanSelectModal
          opened
          onClose={handleBackClick}
          billingPlans={publicBillingPlans}
          gym={salesPortalConfig?.data.gym}
          onSelect={handleBillingPlanSelect}
        />
      </Stack>
    </>
  );
};

export default SalesPortalPackagePage;
